import React, { useCallback, useEffect } from 'react';

import { Route, type RouteProps } from 'react-router-dom';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';

import Spinner from '@atlaskit/spinner/spinner';
import { useOrg } from '@atlassian/ptc-common';
import { logErrorMessage } from '@atlassian/teams-client/sentry';

import RootRedirect from './RootRedirect';

type RouteWithLoadingProps = {
	LoadingComponent?: React.ComponentType;
};

/**
 * A wrapper around the react-router-dom Route component that waits for orgId to be defined before rendering.
 */
export function RouteWithOrgIdLoading({
	LoadingComponent,
	...props
}: RouteProps & RouteWithLoadingProps): JSX.Element {
	const [{ orgId }] = useOrg();
	const [timedOut, setTimedOut] = React.useState(false);

	const handleNoOrgId = useCallback(() => {
		logErrorMessage('No orgId found after 10 seconds', {
			...props,
		});
		setTimedOut(true);
	}, [setTimedOut, props]);

	useEffect(() => {
		if (orgId) {
			return;
		}

		const timeout = setTimeout(() => {
			if (!orgId) {
				handleNoOrgId();
			}
		}, 10000);

		return () => {
			clearTimeout(timeout);
		};
	}, [orgId, handleNoOrgId]);

	if (timedOut) {
		return <RootRedirect location={{ search: '' }} />;
	}

	if (!orgId) {
		return LoadingComponent ? (
			<LoadingComponent />
		) : (
			<DefaultLoadingWrapper data-testid="route-with-org-id-loading">
				<Spinner size={'large'} />
			</DefaultLoadingWrapper>
		);
	}

	return <Route {...props} />;
}

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DefaultLoadingWrapper = styled.div`
	height: 100%;
	width: 100%;
	padding-top: 100px;
	display: flex;
	justify-content: center;
	align-items: center;
`;
